import {FC, useState} from 'react';

import {Box, Dialog, DialogContent, Grid, Grid2, styled, Typography} from '@mui/material';
import {useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';

import Button from 'components/button/Button';
import theme from 'config/theme';
import {zIndexes} from 'constants/index';
import {ANALYTICS} from 'contexts/analytics-context';
import SendBrowserEvent from 'services/send-browser-event';
import {CreateAddressInputType, StoreAreas} from 'types';
import {MapWrapper} from 'views/map-wrapper/MapWrapper';

export const StyledTitleContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
})) as typeof Box;

export const StyledAddressDetailContainer = styled(Box)(() => ({
  paddingLeft: 16
})) as typeof Box;

export const StyledConfirmButton = styled(Button)(({theme}) => ({
  height: theme.spacing(6),
  px: 4,
  paddingTop: '10px'
})) as typeof Button;

export const StyledMapWrapperGrid = styled(Grid)(({theme}) => ({
  width: theme.spacing(75),
  height: '86vh'
}));
export const StyledMapWrapperGrid2 = styled(Grid2)(() => ({
  width: '100%',
  height: '81vh'
}));

export const StyledTextOverLayContainer = styled(Grid)(() => ({
  position: 'absolute',
  cursor: 'default',
  transform: 'translate(-50%)',
  zIndex: '12',
  padding: '4px',
  bottom: '10px',
  left: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  backgroundColor: '#FFFFFF',
  minWidth: '185px',
  maxHeigth: '10px',
  fontSize: '13px',
  fontWeight: 'bolder'
})) as typeof Grid;

export const StyledMapWrapperContainer = styled(Box)<{showConfirmBanner?: boolean}>(({showConfirmBanner}) => ({
  position: 'relative',
  height: '180px',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: zIndexes.zIndexSeven
  },
  '&:hover::after': {
    transition: 'all 0.5s',
    backgroundColor: '#0000006b',
    borderRadius: showConfirmBanner ? '0px 0px 10px 10px' : ''
  }
}));

export const DeleteAddressWrapper = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%'
}));

export const DeleteButtonStyled = styled(Button)(({theme}) => ({
  paddingLeft: theme.spacing(2),
  fontSize: theme.typography.fontSize,
  '&:hover': {
    backgroundColor: theme.palette.common.white
  }
}));

export const StyledWarningTypography = styled(Typography)(({theme}) => ({
  textAlign: 'center',
  lineHeight: theme.spacing(2.5),
  fontSize: theme.spacing(1.75)
}));

export const StyledTooltipBox = styled(Box)(({theme}) => ({
  zIndex: zIndexes.zIndexTen,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  position: 'absolute',
  bottom: theme.spacing(18.5),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  left: '50%',
  transform: 'translateX(-50%)',
  [theme.breakpoints.between('mobile', 'tablet')]: {
    marginBottom: theme.spacing(2.5)
  }
}));

export const StyledTooltipMsgTypography = styled(Typography)(({theme}) => ({
  fontSize: theme.spacing(1.75),
  fontWeight: 600,
  lineHeight: theme.spacing(2.5),
  color: theme.palette.common.mds_global_color_white,
  textAlign: 'center'
}));

export const StyledDiv = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

export const StyledAddressFormDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    height: '100%'
  }
});

export const StyledAddressFormDialogContent = styled(DialogContent)<{updatePadding?: boolean}>(({updatePadding}) => ({
  padding: updatePadding ? 0 : undefined,
  overflow: updatePadding ? 'hidden' : undefined
}));

export const MapButtonContainer = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '-webkit-fill-available',
  padding: theme.spacing(2),
  borderTop: 1,
  backgroundColor: theme.palette.common.mds_global_color_white,
  borderColor: theme.palette.common.mds_global_color_white,
  borderTopLeftRadius: '20px',
  borderTopRightRadius: '20px'
});

type AddressFullMapViewProps = {
  setFullMapView?: (arg: boolean) => void;
  filteredStoreArea: StoreAreas[];
  setOpenFullMapView?: React.Dispatch<React.SetStateAction<boolean>>;
  addressString?: string;
  latLng?: {
    lat: number;
    lng: number;
  };
  addAddressEvent: (eventName: string, payload: any) => void;
};

export const AddressFullMapView: FC<AddressFullMapViewProps> = ({
  setFullMapView,
  setOpenFullMapView,
  addressString,
  latLng,
  addAddressEvent
}) => {
  const {t} = useTranslation();

  const {values, setFieldValue} = useFormikContext<CreateAddressInputType>();

  const [isLoading, setIsLoading] = useState(true);
  const [longitude, setLongitude] = useState(0);
  const [latitude, setLatitude] = useState(0);

  const confirmLocation = () => {
    setFieldValue('user_longitude', longitude ?? values.longitude);
    setFieldValue('user_latitude', latitude ?? values.latitude);
    setFullMapView && setFullMapView(false);
    setOpenFullMapView && setOpenFullMapView(false);
    SendBrowserEvent.showSuccessToast(t('message.pinSavedSucces'));
    addAddressEvent(ANALYTICS.ADDRESS_LOCATION_CONFIRMED, {
      address: values,
      coordinates: {lat: latitude ?? values.latitude, lng: longitude ?? values.longitude}
    });
  };

  return (
    <Grid container py={2} spacing={2} alignItems="flex-start">
      <StyledMapWrapperGrid xs={12} mb={1} item>
        <MapWrapper
          latLng={latLng}
          addressString={addressString}
          onClick={({lat, lng}) => {
            setLongitude(lng());
            setLatitude(lat());
            setIsLoading(false);
          }}
          onAddressChange={({lat, lng}) => {
            setFieldValue('longitude', lng());
            setFieldValue('latitude', lat());
          }}
          markerPinAllowed={true}
          //  mapProps={{restriction: restrictedThaiBounds}}

          address={values}
          addAddressEvent={addAddressEvent}
        />
      </StyledMapWrapperGrid>
      <Grid xs={12} item>
        <MapButtonContainer>
          <Box
            style={{
              backgroundColor: '#FFEBE5',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#D84018',
              padding: '12px',
              marginBottom: '16px'
            }}
          >
            <Typography sx={{fontWeight: '600', fontSize: '13px', textAlign: 'center'}}>
              {t('message.confirmLocationMessage')}
            </Typography>
          </Box>
          <StyledConfirmButton
            fullWidth
            shape="rounded"
            variant="contained"
            disabled={isLoading}
            onClick={confirmLocation}
          >
            {t('button.useLocation')}
          </StyledConfirmButton>
        </MapButtonContainer>
      </Grid>
    </Grid>
  );
};
